import React, { useState, useEffect } from 'react';
import { IoLockClosedOutline, IoWarningOutline } from 'react-icons/io5';

import { API_URL } from '../services/api';
import { ENABLE_DEV_MODE } from '../config/features';

// -- URL trigger handling -----------------------------------------------------
// Run synchronously at module load — BEFORE React renders anything.
// This prevents React Router's catch-all (path="*" → /login) from firing
// first and swallowing /dev or /removedev before SecurityGate can process them.
// Skipped entirely when ENABLE_DEV_MODE is false.
(function handleSecurityUrlTriggers() {
  if (!ENABLE_DEV_MODE) return;

  const path = window.location.pathname;

  if (path.endsWith('/dev')) {
    localStorage.setItem('debug_mode', 'true');
    const cleanPath = path.replace(/\/dev$/, '');
    window.location.replace(cleanPath || '/');
    return;
  }

  if (path.endsWith('/removedev')) {
    localStorage.removeItem('debug_mode');
    localStorage.removeItem('debug_key');
    localStorage.removeItem('debug_blocked');
    const cleanPath = path.replace(/\/removedev$/, '');
    window.location.replace(cleanPath || '/');
  }
})();

// Inner component — only mounted when ENABLE_DEV_MODE is true.
// All hooks live here so they are never called when dev mode is off.
const SecurityGateInner = ({ children }) => {
  const [isAuthorized, setIsAuthorized] = useState(
    !localStorage.getItem('debug_mode'),
  );
  const [isBlocked, setIsBlocked] = useState(
    localStorage.getItem('debug_blocked') === 'true',
  );
  const [keyInput, setKeyInput] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    // Standard Authorization Logic (URL triggers already handled above)
    const hasDebugMode = localStorage.getItem('debug_mode') === 'true';
    const hasValidKey = !!localStorage.getItem('debug_key');
    const isActuallyBlocked = localStorage.getItem('debug_blocked') === 'true';

    if (!hasDebugMode) {
      setIsAuthorized(true);
    } else if (hasValidKey && !isActuallyBlocked) {
      const savedKey = localStorage.getItem('debug_key');
      if (savedKey) verifyKey(savedKey);
    } else {
      setIsAuthorized(false);
    }
  }, [isBlocked]);

  const verifyKey = async (key) => {
    setIsLoading(true);
    try {
      const response = await fetch(`${API_URL}/verify-debug-key`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'x-debug-key': key.trim(),
        },
      });

      if (response.ok) {
        localStorage.setItem('debug_key', key.trim());
        setIsAuthorized(true);
      } else {
        localStorage.setItem('debug_blocked', 'true');
        setIsBlocked(true);
        setError('Invalid security key. Access permanently blocked.');
      }
    } catch (err) {
      setError('Verification failed. Check your connection.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (keyInput.trim()) verifyKey(keyInput.trim());
  };

  // If we're blocked, show the "Access Blocked" screen
  if (isBlocked) {
    return (
      <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-[#09090b] text-[#d4d4d8]">
        <div className="animate-in fade-in zoom-in w-full max-w-md space-y-6 p-8 text-center duration-300">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full border border-red-500/20 bg-red-500/10 text-red-500">
            <IoWarningOutline size={32} />
          </div>
          <h1 className="text-2xl font-black uppercase tracking-tighter text-white">
            Access Permanently Blocked
          </h1>
          <p className="text-sm leading-relaxed text-zinc-400">
            You have entered an incorrect security key. For security reasons,
            access to this environment has been restricted.
          </p>
          <div className="rounded-lg border border-zinc-800 bg-zinc-900/50 p-4 text-left font-mono text-[11px] text-zinc-500">
            Error: Unauthorized_Debug_Access_Attempt
            <br />
            Timestamp: {new Date().toISOString()}
            <br />
            Status: LOCKED
          </div>
          <p className="text-[10px] text-zinc-600">
            Contact your system administrator to reset your access.
          </p>
        </div>
      </div>
    );
  }

  // If we're not authorized yet, show the "Enter Key" screen
  if (!isAuthorized) {
    return (
      <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-[#09090b] text-[#d4d4d8]">
        <div className="animate-in fade-in slide-in-from-bottom-4 w-full max-w-sm space-y-8 p-8 duration-500">
          <div className="space-y-2 text-center">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-xl border border-[#5652CF]/20 bg-[#5652CF]/10 text-[#5652CF]">
              <IoLockClosedOutline size={24} />
            </div>
            <h1 className="text-xl font-black uppercase tracking-tighter text-white">
              Security Check
            </h1>
            <p className="text-xs text-zinc-500">
              Please enter your developer security key to proceed.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-1">
              <input
                type="password"
                placeholder="Enter Security Key"
                value={keyInput}
                onChange={(e) => setKeyInput(e.target.value)}
                className="w-full rounded-lg border border-zinc-800 bg-zinc-900 px-4 py-3 text-sm transition-all placeholder:text-zinc-700 focus:border-transparent focus:outline-none focus:ring-1 focus:ring-[#5652CF]"
              />
              {error && (
                <p className="mt-2 text-[10px] font-bold text-red-500">
                  {error}
                </p>
              )}
            </div>
            <button
              type="submit"
              disabled={isLoading}
              className="w-full rounded-lg bg-[#5652CF] py-3 text-xs font-bold uppercase tracking-widest text-white transition-all hover:bg-[#5652CF]/90 disabled:opacity-50"
            >
              {isLoading ? 'Verifying...' : 'Unlock Environment'}
            </button>
          </form>

          <p className="text-center text-[10px] text-zinc-600">
            One-time verification required for all active sessions.
          </p>
        </div>
      </div>
    );
  }

  // If authorized, render the app
  return children;
};

// Outer wrapper — the only thing that checks the feature flag.
// When ENABLE_DEV_MODE is false, SecurityGateInner is never mounted:
// zero hooks run, zero localStorage reads, zero API calls.
const SecurityGate = ({ children }) => {
  if (!ENABLE_DEV_MODE) return children;
  return <SecurityGateInner>{children}</SecurityGateInner>;
};

export default SecurityGate;
